import React, { useCallback, useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { CreateWatchContext, ICreateWatchContext } from '../createWatchState/createWatchContext';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { convertUndefinedFields } from 'utils/helpers';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { createWatch } from 'backend/watches';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		resetFields,
		showErrorModalWithInfo,
		changeTypeOfModalWindow,
		dataForSubmit,
		typeOfModalWindow,
		errorResponseData,
	} = useContext<ICreateWatchContext>(CreateWatchContext);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await createWatch(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.SERIAL_NUMBER_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
					changeTypeOfModalWindow(null);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!errorResponseData}>
			{typeOfModalWindow === 'toConfirmFieldsClearing' && (
				<ModalToConfirm
					onConsentEvent={resetFields}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="cancelSaveInfo"
				/>
			)}
			{typeOfModalWindow === 'toConfirmSubmit' && (
				<ModalToConfirm
					onConsentEvent={handleConfirm}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="saveInfo"
				/>
			)}
			{typeOfModalWindow === 'success' && (
				<ModalSentSuccessfully onConsentEvent={resetFields} isVisible={true} description="savedSuccessfully" />
			)}
			{typeOfModalWindow === 'error' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorWhileSaving" />
			)}
			{errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description={errorResponseData.Type}
					translationOptions={{
						value: errorResponseData.Metadata.serialNumber,
					}}
				/>
			)}
		</ModalWrapper>
	);
};
